import { useQueryClient } from '@tanstack/react-query'

import { TSessionDetails } from '~/features/auth/models'
import { queryKeys as authQueryKeys } from '~/features/auth/utils'
import { ECognitoGroups } from '~/models/auth'

/**
 * True when the session has an access token and at least one Cognito group.
 * Use this to gate access to JWT-only services (internal API, agents, etc.)
 * and to avoid redirecting to protected routes before the session is ready.
 */
const useHasValidAuthSession = () => {
  const queryClient = useQueryClient()

  const sessionData = queryClient.getQueryData<TSessionDetails>(
    authQueryKeys.authSession(),
  )
  const isValidGroup =
    Array.isArray(sessionData?.groups) &&
    Object.values(ECognitoGroups).some((cognitoGroup) =>
      sessionData.groups.includes(cognitoGroup),
    )

  const hasValidAuthSession =
    !!sessionData?.accessToken && !!sessionData?.groups?.length && isValidGroup

  // True as soon as a user is signed in (has a Cognito identity), even before
  // groups land in the token. Lets callers tell a guest (no session) apart from
  // an authenticated-but-not-yet-grouped user, so they can wait for the grouped
  // token instead of prematurely hitting a fallback backend.
  const isLoggedIn = !!sessionData?.cognitoUid

  return { hasValidAuthSession, isLoggedIn }
}

export default useHasValidAuthSession
